<template lang="pug">
.analytics__model-info
    v-card.mb-2
        v-card-title
            | ML Model Version: {{ model.model_version }}
            v-chip.ml-2(v-if="model.is_active" color="green" small) Active
        v-card-text
            v-row
                v-col
                    v-simple-table
                        thead
                            tr
                                th Epochs
                                th MAE
                                th MSE
                                th MAPE
                                th RMSE
                        tbody
                            tr
                                td {{ formattedParams.epochs_ran }}
                                td {{ formattedParams.mae }}
                                td {{ formattedParams.mse }}
                                td {{ formattedParams.mape }}%
                                td {{ formattedParams.rmse }}

                    ul
                        li(
                            @click="showHyperParams = !showHyperParams"
                            style="cursor: pointer; user-select: none; text-decoration: underline;"
                        )
                            | Hyper Params
                            v-icon.ml-1 {{ showHyperParams ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                            ul(v-show="showHyperParams")
                                li(
                                    v-for="(val, key) in formattedParams.hyper_params"
                                    :key="key"
                                    class="d-flex justify-space-between"
                                    style="width: 260px;"
                                )
                                    span {{ key }}
                                    span(style="text-align: right; min-width: 80px; display: inline-block;") {{ val }}
            v-row
                v-col
                    div(v-if="sortedForecasts.length === 0")
                        v-alert(
                            color="info"
                            text
                            dense
                            border="left"
                        ) No forecast metrics are available for this model.
                    div(v-else)
                        v-simple-table()
                            thead
                                tr
                                    th Start date
                                    th MAE
                                    th RMSE
                                    th MAPE
                                    th R2
                                    th MBE
                                    th Tracking<br>Signal
                                    th Model Used
                            tbody
                                tr(
                                    v-for="forecast in sortedForecasts"
                                    :key="forecast.id"
                                )
                                    td {{ formatStartDate(forecast.start_date, forecast.id) }}
                                    td {{ formatValue(forecast.mean_absolute_error) }}
                                    td {{ formatValue(forecast.root_mean_square_error) }}
                                    td {{ formatValue(forecast.mean_absolute_percentage_error) }}%
                                    td {{ formatValue(forecast.r2) }}
                                    td {{ formatValue(forecast.mean_bias_error) }}
                                    td {{ formatValue(forecast.tracking_signal) }}
                                    td {{ forecast.model_used }}


                        span.d-block.text-caption.pl-2.mt-1(style="border-left: 2px solid #2196F3; padding-left: 8px;")
                            | Snapshots of 120 hr that have been matched to demand
                        span.d-block.text-caption.pl-2.mt-1(style="border-left: 2px solid rgb(168, 51, 22); padding-left: 8px;")
                            | ** Indicates forecast before correction
</template>

<script>
import {DateTime} from 'luxon';
export default {
    name: 'ModelInfo',
    props: {
        model: { type: Object, required: true },
        timezone: { type: String, required: true }
    },
    computed: {
        sortedForecasts () {
            if (!this.model.forecasts || !Array.isArray(this.model.forecasts)) {
                console.log('No forecasts or not an array for model:', this.model.model_version);
                return [];
            }

            return [...this.model.forecasts].sort((a, b) => {
                return new Date(b.createdAt) - new Date(a.createdAt);
            });
        },
        formattedParams () {
            let params = {
                epochs_ran: 'N/A',
                mae: 'N/A',
                mse: 'N/A',
                mape: 'N/A',
                rmse: 'N/A',
                hyper_params: {}
            };

            if (!this.model.other_parameters) {
                return params;
            }

            try {
                const parsedParams = typeof this.model.other_parameters === 'string'
                    ? JSON.parse(this.model.other_parameters)
                    : this.model.other_parameters;

                if (parsedParams) {
                    // Extract specific metrics if available
                    if (parsedParams.epochs_ran) params.epochs_ran = parsedParams.epochs_ran;
                    if (parsedParams.mae) params.mae = this.formatValue(parsedParams.mae);
                    if (parsedParams.mse) params.mse = this.formatValue(parsedParams.mse);
                    if (parsedParams.mape) params.mape = this.formatValue(parsedParams.mape);
                    if (parsedParams.rmse) params.rmse = this.formatValue(parsedParams.rmse);

                    // Extract hyperparameters if available
                    if (parsedParams.hyper_params) {
                        params.hyper_params = parsedParams.hyper_params;
                    } else {
                        // If no specific hyper_params object, filter out the metrics and use rest as hyper params
                        const metricsKeys = ['epochs_ran', 'mae', 'mse', 'mape', 'rmse'];
                        params.hyper_params = Object.fromEntries(
                            Object.entries(parsedParams)
                                .filter(([key]) => !metricsKeys.includes(key))
                        );
                    }
                }

                return params;
            } catch (err) {
                console.error('Error parsing model parameters:', err);
                return params;
            }
        },

        browserTimezone () {
            return DateTime.local().zoneName;
        }
    },
    methods: {
        formatValue (val) {
            if (val === null || val === undefined || isNaN(val)) return 'N/A';
            return Number(val).toFixed(3);
        },
        formatStartDate (date, forecastInfoId) {
            if (!date) return 'N/A';

            try{
                /** @type {DateTime} */
                let dt = this.formatDate(date);
                // get the hours
                if(!dt.isValid) {
                    return 'Invalid date';
                }
                if(dt.hour === 3){
                    dt = dt.plus({ hours: 1 }).setZone(this.browserTimezone);
                    return dt.isValid ? dt.toFormat('MM/dd hh:mm a ZZZZ') : 'Invalid date';
                }
                if(dt.hour !== 4){
                    // its probably messed up from previous code errors
                    dt = dt.setZone(this.browserTimezone);
                    return  `${dt.toFormat('MM/dd hh:mm a ZZZZ')}**`;
                }
                dt = dt.setZone(this.browserTimezone);
                return  dt.toFormat('MM/dd hh:mm a ZZZZ');

            } catch (e) {
                return 'Invalid date';
            }
        },

        /**
         *
         * @param {string} date - ISO timestamp
         * @returns {DateTime} - Luxon DateTime object in UTC
         */
        formatDate (date) {
            // the date is going to have the wrong offset because it is in UTC even though Sequelize thinks it is in the server timezone
            if (!date) return 'N/A';
            let dt = DateTime.fromFormat(
                date.replace('T', ' ').replace(/Z|[+-]\d{2}:?\d{2}$/, ''),
                "yyyy-MM-dd HH:mm:ss.SSS",
                { zone: 'UTC' }
            );
            if (!dt.isValid) {
                // fallback: try parsing as ISO, then force to UTC
                dt = DateTime.fromISO(date, { zone: 'utc' });
            }
            return dt;
        }
    },
    data () {
        return {
            showHyperParams: false,
            correctedForecastInfoId: 33
        };
    }
};
</script>
